/**
 * Every address in the app, in one place, in one language (F-142).
 *
 * Leo, 2026-09-17: *"Im logged in and the ui is in English but the slug says investigacion."*
 *
 * **The locale belongs in the content, not in the address.** The sections were named in
 * Spanish because the product's first language is Spanish, which was true and is still the
 * wrong place to say it: the interface is translated at render time from `i18n.ts`, so an
 * English-speaking learner reads an English screen at a Spanish URL — and translating the
 * slugs per locale would be worse again, because the same screen would then have two
 * addresses and neither could be shared, bookmarked or written into a support reply without
 * asking first which language the reader uses. One stable path per screen, the same for
 * everybody, with the language living entirely in what the page says.
 *
 * English rather than Spanish for that stable form because the rest of the vocabulary a URL
 * already shares — `/admin`, `/chat`, `/plan`, every route the API answers — is English, and
 * a half-translated address bar is exactly what produced the complaint.
 *
 * **Every old path still works**, redirected in `App.tsx` with its query string and fragment
 * intact. They are in browser histories, in home screens saved before today and in anything
 * anybody bookmarked, and a 404 at a URL that worked yesterday is indistinguishable from the
 * app being broken.
 */
export const ROUTES = {
  home: '/',
  signIn: '/sign-in',
  chat: '/chat',
  progress: '/progress',
  plan: '/plan',
  research: '/research',
  settings: '/settings',
  profile: '/profile',
  /**
   * The one-click unsubscribe landing, and the one path deliberately left in Spanish.
   *
   * It is not a screen a learner navigates to: the API mints it into the footer of every
   * message it sends (`handler.ts`, the `List-Unsubscribe` header), so the addresses that
   * matter are the ones already sitting in inboxes. Renaming it would either break those or
   * leave two spellings of it forever, and neither buys a learner anything — nobody reads
   * this one out of an address bar, they arrive by tapping a link in mail they want stopped.
   */
  unsubscribe: '/baja',
} as const;

/**
 * Where the addresses that used to exist now go.
 *
 * Keys are dead paths and values are live ones, and `App.tsx` mounts one redirect per entry
 * by mapping over this object rather than by writing them out — which is what actually stops
 * a rename landing without its redirect, and a 404 for everybody whose home screen still
 * holds the old icon.
 *
 * `routes.test.ts` holds the table and not the mounting: that every target is a real route,
 * that no path is both dead and live, that the renamed five are all here. **Whether this file
 * is wired into the router at all is unasserted** — `App.tsx` is not in the test-compiled set
 * (see `tsconfig.test.json`) and there is no DOM harness here, so the mapping above is a
 * convention held by reading rather than a guard held by a test.
 */
export const MOVED: Readonly<Record<string, string>> = {
  '/entrar': ROUTES.signIn,
  '/progreso': ROUTES.progress,
  '/investigacion': ROUTES.research,
  '/ajustes': ROUTES.settings,
  '/perfil': ROUTES.profile,
  /*
   * The one screen the five sections were cut out of. It predates this rename and is kept
   * for the same reason as the rest of them.
   */
  '/objetivos': ROUTES.plan,
};

/**
 * The panels a link can land on, rather than the top of the screen they live on (F-143).
 *
 * Leo, 2026-09-17: *"when i click on set it up for notifactions it just takes me to setting
 * not specifically to the place to set notificatons up."*
 *
 * A fragment rather than a route, because these are sections of the configuration screen and
 * not screens of their own: somebody who came to set one of them up is usually about to look
 * at the other. `Disclosure` reads the fragment, opens the section — including one this
 * learner folded away weeks ago — and moves focus to it.
 *
 * **One constant, because one link exists.** The notification switch is addressable too —
 * `/settings#notifications`, from the `anchor` on its panel — and there is deliberately no
 * `SETTINGS_NOTIFICATIONS` here to point at it: an exported name with no caller is surface
 * nobody is maintaining, and the next thing that wants to link there can add it in the same
 * change as the link.
 */
export const SETTINGS_SCHEDULE = `${ROUTES.settings}#schedule`;
